import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import {
  ShoppingBag,
  MapPin,
  DollarSign,
  Clock,
  Home,
  Send,
  Eye,
  CheckCircle,
  Building2,
  Loader2,
  ArrowLeft,
  Target,
  Users,
  Briefcase,
  FileText,
  FileSearch,
  TrendingUp,
  Settings,
  LogOut,
  Menu,
  X
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Realtor Sidebar
function RealtorSidebar({ realtor, onLogout }) {
  const [collapsed, setCollapsed] = useState(false);
  
  const navSections = [
    {
      title: "Operations",
      items: [
        { icon: Target, label: "Command Center", path: "/realtor/dashboard" },
        { icon: ShoppingBag, label: "Deal Opportunities", path: "/realtor/opportunities", active: true },
        { icon: FileText, label: "Active Listings", path: "/realtor/listings" },
        { icon: Briefcase, label: "Transactions", path: "/realtor/transactions" },
      ]
    },
    {
      title: "Network",
      items: [
        { icon: Users, label: "Buyer Pipeline", path: "/realtor/buyers" },
        { icon: Building2, label: "Seller Pipeline", path: "/realtor/sellers" },
        { icon: FileSearch, label: "Title Network", path: "/realtor/title-services" },
      ]
    },
    {
      title: "Intelligence",
      items: [
        { icon: DollarSign, label: "Revenue Stream", path: "/realtor/commission" },
        { icon: TrendingUp, label: "Performance", path: "/realtor/analytics" },
        { icon: Settings, label: "Configuration", path: "/realtor/settings" },
      ]
    }
  ];

  return (
    <aside className={`fixed left-0 top-0 h-full bg-[#0A0A0A]/80 backdrop-blur-sm border-r border-white/[0.06] transition-all z-40 ${
      collapsed ? "w-16" : "w-64"
    }`}>
      <div className="p-4 border-b border-white/[0.06] flex items-center justify-between">
        {!collapsed && (
          <Link to="/realtor/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
              <Home className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <div>
              <span className="font-semibold text-white text-sm">REALTOR</span>
              <p className="text-[8px] text-white/30 uppercase tracking-wider">Portal</p>
            </div>
          </Link>
        )}
        <button onClick={() => setCollapsed(!collapsed)} className="text-white/50 hover:text-white p-1">
          {collapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
        </button>
      </div>

      <nav className="p-2">
        {navSections.map((section, sIdx) => (
          <div key={sIdx} className="mb-4">
            {!collapsed && (
              <p className="px-3 py-2 text-[9px] text-white/30 uppercase tracking-wider font-semibold">
                {section.title}
              </p>
            )}
            <div className="space-y-0.5">
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive = item.active || window.location.pathname === item.path;
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2.5 transition-all duration-300 ${
                      isActive 
                        ? "bg-[#D4AF37]/10 text-[#D4AF37] border-l-2 border-[#D4AF37]" 
                        : "text-white/50 hover:text-white hover:bg-white/[0.02]"
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    {!collapsed && <span className="text-sm">{item.label}</span>}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {!collapsed && (
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/[0.06]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#0A0A0A]/80 rounded-full flex items-center justify-center">
              <span className="text-[#D4AF37] font-bold">{realtor?.name?.charAt(0) || "R"}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{realtor?.name}</p>
              <p className="text-white/40 text-xs truncate">{realtor?.brokerage}</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2 text-white/50 hover:text-[#FF3B30] py-2 border border-white/[0.06] hover:border-red-500/50 transition-all"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm">Logout</span>
          </button>
        </div>
      )}
    </aside>
  );
}

// Opportunity Card Component
function OpportunityCard({ opportunity, onBid, alreadyBid }) {
  return (
    <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] p-5 hover:border-[#D4AF37]/30 transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <MapPin className="w-4 h-4 text-[#D4AF37]" />
            <h3 className="text-white font-medium">{opportunity.property_address}</h3>
          </div>
          <p className="text-white/40 text-xs">
            Request ID: {opportunity.request_id}
          </p>
        </div>
        <div className="px-2 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded">
          <span className="text-[10px] text-emerald-400 font-semibold uppercase">Open</span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <p className="text-[10px] text-white/40 uppercase mb-1">Listing Price</p>
          <p className="text-white font-mono">{formatCurrency(opportunity.listing_price)}</p>
        </div>
        <div>
          <p className="text-[10px] text-white/40 uppercase mb-1">Commission</p>
          <p className="text-[#D4AF37] font-mono">{opportunity.commission_percent || 3}%</p>
        </div>
        <div>
          <p className="text-[10px] text-white/40 uppercase mb-1">Timeline</p>
          <p className="text-white">{opportunity.timeline_days || 90} days</p>
        </div>
      </div>

      {opportunity.notes && (
        <div className="mb-4 p-3 bg-black/50 border border-white/[0.06] rounded">
          <p className="text-[10px] text-white/40 uppercase mb-1">Operator Notes</p>
          <p className="text-white/70 text-sm">{opportunity.notes}</p>
        </div>
      )}

      <div className="flex items-center gap-3">
        {alreadyBid ? (
          <div className="flex items-center gap-2 text-emerald-400">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">Interest Submitted</span>
          </div>
        ) : (
          <Button
            onClick={() => onBid(opportunity)}
            className="flex-1 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold"
          >
            <Send className="w-4 h-4 mr-2" />
            Express Interest
          </Button>
        )}
      </div>
    </div>
  );
}

// Bid Modal
function BidModal({ opportunity, onClose, onSubmit, submitting }) {
  const [form, setForm] = useState({
    proposed_commission: opportunity?.commission_percent || 3,
    marketing_plan: "",
    estimated_days_to_sell: 60,
    notes: ""
  });

  const handleSubmit = () => {
    if (!form.proposed_commission) {
      toast.error("Please enter a commission rate");
      return;
    }
    onSubmit(form);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-lg max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Express Interest</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-4 p-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded">
          <p className="text-[#D4AF37] font-medium text-sm">{opportunity?.property_address}</p>
          <p className="text-white/50 text-xs mt-1">
            Target: {formatCurrency(opportunity?.listing_price)} • {opportunity?.timeline_days || 90} day timeline
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-white/70 text-sm mb-1.5 block">Your Commission Rate (%)</label>
            <Input
              type="number"
              step="0.25"
              value={form.proposed_commission}
              onChange={(e) => setForm({...form, proposed_commission: parseFloat(e.target.value)})}
              className="bg-black border-white/[0.08] text-white"
              placeholder="e.g., 2.5"
            />
          </div>

          <div>
            <label className="text-white/70 text-sm mb-1.5 block">Estimated Days to Sell</label>
            <Input
              type="number"
              value={form.estimated_days_to_sell}
              onChange={(e) => setForm({...form, estimated_days_to_sell: parseInt(e.target.value)})}
              className="bg-black border-white/[0.08] text-white"
              placeholder="e.g., 60"
            />
          </div>

          <div>
            <label className="text-white/70 text-sm mb-1.5 block">Marketing Plan (Optional)</label>
            <textarea
              value={form.marketing_plan}
              onChange={(e) => setForm({...form, marketing_plan: e.target.value})}
              className="w-full bg-black border border-white/[0.08] rounded-md text-white p-3 text-sm min-h-[80px] resize-none"
              placeholder="Describe your marketing approach..."
            />
          </div>

          <div>
            <label className="text-white/70 text-sm mb-1.5 block">Additional Notes (Optional)</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({...form, notes: e.target.value})}
              className="w-full bg-black border border-white/[0.08] rounded-md text-white p-3 text-sm min-h-[60px] resize-none"
              placeholder="Any additional information..."
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={onClose} className="flex-1 border-white/[0.08] text-white hover:bg-white/[0.04]">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Submit Interest
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function RealtorOpportunitiesPage() {
  const navigate = useNavigate();
  const [realtor, setRealtor] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [profileRes, opportunitiesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/realtor/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/realtor/listing-opportunities`, { withCredentials: true })
      ]);
      setRealtor(profileRes.data);
      setOpportunities(opportunitiesRes.data || []);
      
      // Fetch my bids to check which opportunities I've already bid on
      try {
        const bidsRes = await axios.get(`${BACKEND_URL}/api/realtor/marketplace/my-bids`, { withCredentials: true });
        setMyBids(bidsRes.data || []);
      } catch (e) {
        // Bids endpoint may not exist for listing requests
      }
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/realtor");
      } else {
        toast.error("Failed to load opportunities");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/realtor/auth/logout`, {}, { withCredentials: true });
      navigate("/realtor");
    } catch (error) {
      navigate("/realtor");
    }
  };

  const handleSubmitBid = async (formData) => {
    if (!selectedOpportunity) return;
    
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/realtor/express-interest`,
        {
          listing_request_id: selectedOpportunity.request_id,
          ...formData
        },
        { withCredentials: true }
      );
      toast.success("Interest submitted! The operator will review your bid.");
      setSelectedOpportunity(null);
      fetchData(); // Refresh to show updated status
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit interest");
    } finally {
      setSubmitting(false);
    }
  };

  // Check if already bid on an opportunity
  const hasAlreadyBid = (requestId) => {
    return opportunities.find(o => o.request_id === requestId)?.interested_realtors?.includes(realtor?.realtor_id);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <RealtorSidebar realtor={realtor} onLogout={handleLogout} />
      
      <main className="ml-64 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">
              Deal <span className="text-[#D4AF37]">Opportunities</span>
            </h1>
            <p className="text-white/50 text-sm mt-1">
              Listing requests from investors seeking realtors
            </p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/[0.08] border border-emerald-500/20 rounded">
            <div className="relative w-2 h-2">
              <div className="absolute inset-0 bg-emerald-400 rounded-full" />
              <div className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-75" />
            </div>
            <span className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider">
              {opportunities.length} Open
            </span>
          </div>
        </div>

        {/* Opportunities Grid */}
        {opportunities.length === 0 ? (
          <div className="text-center py-16 bg-[#0A0A0A]/80 border border-white/[0.06] rounded">
            <ShoppingBag className="w-12 h-12 text-white/30 mx-auto mb-3" />
            <p className="text-white/50 text-lg mb-2">No Open Opportunities</p>
            <p className="text-white/30 text-sm">
              Check back later for listing requests from investors
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {opportunities.map((opp) => (
              <OpportunityCard
                key={opp.request_id}
                opportunity={opp}
                onBid={setSelectedOpportunity}
                alreadyBid={hasAlreadyBid(opp.request_id)}
              />
            ))}
          </div>
        )}
      </main>

      {/* Bid Modal */}
      {selectedOpportunity && (
        <BidModal
          opportunity={selectedOpportunity}
          onClose={() => setSelectedOpportunity(null)}
          onSubmit={handleSubmitBid}
          submitting={submitting}
        />
      )}
    </div>
  );
}
